'use client';

import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { EnhancedPasswordInputWrapper } from '@web-libs/ui/enhanced-password-input/enhanced-password-input-wrapper';
import { PasswordRulesConfig } from '@tremaze/enhanced-password-input';
import {
  defineFormField,
  FormFieldProps,
} from '@web-libs/core/forms/ui/fields/shared/form-field.types';
import {
  TextFieldColor,
  textFieldColorMap,
} from '@web-libs/core/forms/ui/fields/text-field/text-field';
import { getCssVar } from '@web-libs/features/colors/application/colors';
import textFieldStyles from '../text-field/text-field.module.css';
import { FieldError } from '@web-libs/core/forms/ui/fields/shared/field-error';
import { useHttpContext } from '@web-libs/core/http/infrastructure/client-side/http.context';

export type EnhancedPasswordFieldProps = FormFieldProps<string> & {
  width?: number;
  fitParent?: boolean;
  color?: TextFieldColor;
  style?: CSSProperties;
  showStrengthIndicator?: boolean;
  showToggle?: boolean;
  showRepeatPassword?: boolean;
  rules?: PasswordRulesConfig;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
};

const _EnhancedPasswordField = (
  props: EnhancedPasswordFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const {
    onChange,
    name,
    value,
    error,
    label,
    fitParent = false,
    width,
    color = 'lightOnPrimary',
    showStrengthIndicator = true,
    showToggle = true,
    showRepeatPassword = true,
    rules,
    placeholder,
    disabled,
    required,
    style,
  } = props;

  // Get HTTP client from context
  const httpClient = useHttpContext();

  // State for password policy from API
  const [passwordPolicy, setPasswordPolicy] = useState<
    PasswordRulesConfig | undefined
  >(rules);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(false);

  // Type for API response
  interface PasswordPolicyResponse {
    id: string;
    tenantId: string;
    minLength: number;
    uppercaseRequired: boolean;
    lowercaseRequired: boolean;
    numbersRequired: boolean;
    specialCharactersRequired: boolean;
  }

  // Convert API response to PasswordRulesConfig
  const convertToPasswordRules = (
    policy: PasswordPolicyResponse,
  ): PasswordRulesConfig => {
    return {
      minLength: policy.minLength || 8,
      requireUppercase: policy.uppercaseRequired || false,
      requireLowercase: policy.lowercaseRequired || false,
      requireNumbers: policy.numbersRequired || false,
      requireSpecialChars: policy.specialCharactersRequired || false,
    };
  };

  // Fetch password policy from API
  useEffect(() => {
    // Only fetch if rules are not provided as props
    if (!rules) {
      setIsLoadingPolicy(true);

      httpClient
        .get<PasswordPolicyResponse>('/public/tenants/current/passwordPolicy')
        .then((data) => {
          const convertedRules = convertToPasswordRules(data);
          setPasswordPolicy(convertedRules);
        })
        .catch((error) => {
          console.error('Error fetching password policy:', error);
          // Set default rules on error
          setPasswordPolicy({
            minLength: 8,
            requireUppercase: false,
            requireLowercase: false,
            requireNumbers: false,
            requireSpecialChars: false,
          });
        })
        .finally(() => {
          setIsLoadingPolicy(false);
        });
    }
  }, [rules, httpClient]);

  const handleChange = useCallback(
    (newValue: string | null) => {
      // Convert to a synthetic event-like object for React Hook Form
      const event = {
        target: {
          name,
          value: newValue || '',
        },
        type: 'change',
      } as any;

      if (onChange) {
        onChange(event);
      }
    },
    [onChange, name],
  );

  // Style calculations to match TextField
  const _wrapperStyle: CSSProperties = { ...style };
  if (width) {
    _wrapperStyle.width = width;
  } else if (fitParent) {
    _wrapperStyle.flexGrow = style?.flexGrow ?? 1;
    _wrapperStyle.width = '100%';
  } else {
    _wrapperStyle.width = 300;
  }

  const theme = textFieldColorMap[color] ?? textFieldColorMap.dark!;
  const borderColor = error ? getCssVar('warn') : getCssVar(theme.baseColor);

  const errorStyle = error ? textFieldStyles.error : '';
  const containerClass = `${textFieldStyles.fieldContainer} ${errorStyle}`;

  const containerStyle: CSSProperties = {
    borderColor: borderColor,
    border: 'none',
  };

  // Map the field color to the Angular component color format
  let angularColor: 'light' | 'dark' | 'white' = 'white';
  if (color === 'dark') {
    angularColor = 'dark';
  } else if (color === 'lightOnPrimary' || color === 'light') {
    angularColor = 'white';
  }

  return (
    <div style={_wrapperStyle}>
      <div style={containerStyle} className={containerClass}>
        <EnhancedPasswordInputWrapper
          value={value}
          onChange={handleChange}
          placeholder={placeholder || label || 'Passwort'}
          showStrengthIndicator={showStrengthIndicator}
          showToggle={showToggle}
          showRepeatPassword={showRepeatPassword}
          rules={passwordPolicy}
          disabled={disabled || isLoadingPolicy}
          required={required}
          color={angularColor}
        />
      </div>
      <FieldError error={error} />
    </div>
  );
};

export const EnhancedPasswordField = defineFormField<
  EnhancedPasswordFieldProps,
  HTMLInputElement,
  string
>(_EnhancedPasswordField);
